


import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X } from 'lucide-react';
import './arrondissement.css';
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";
import AxiosInstance from "../AxiosInstance";
const ITEMS_PER_PAGE_OPTIONS = [10, 20, 30];

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [hierarchy, setHierarchy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [showInactive, setShowInactive] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE_OPTIONS[0]);
  const [profil, setProfil] = useState(null);

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    icon: '',
    isActive: true,
    parentCategory: '',
    order: 0,
  });

  const [deleteConfirm, setDeleteConfirm] = useState({
    show: false,
    id: null,
    name: '',
    force: false,
  });

  // Fetch user profile
  useEffect(() => {
    AxiosInstance.get('/api/users/profile')
      .then(response => setProfil(response.data))
      .catch(error => console.error(error));
  }, []);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) navigate("/");
  }, [isAuthenticated, navigate]);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearchTerm(searchTerm), 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Fetch categories or hierarchy
  useEffect(() => {
    if (viewMode === 'hierarchy') fetchHierarchy();
    else fetchCategories();
  }, [viewMode, debouncedSearchTerm, showInactive]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.get('/api/categories');
      setCategories(response.data.data || response.data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erreur lors du chargement des catégories');
    } finally {
      setLoading(false);
    }
  };

  const fetchHierarchy = async () => {
    try {
      setLoading(true);
      const response = await AxiosInstance.get(`/api/categories/hierarchy?includeInactive=${showInactive}`);
      setHierarchy(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        parentCategory: formData.parentCategory || undefined,
        order: Number(formData.order),
      };

      if (editingItem) {
        await AxiosInstance.put(`/api/categories/${editingItem._id}`, payload);
      } else {
        await AxiosInstance.post('/api/categories', payload);
      }

      viewMode === 'hierarchy' ? await fetchHierarchy() : await fetchCategories();
      setSuccessMessage(editingItem ? 'Catégorie modifiée avec succès ✅' : 'Catégorie créée avec succès ✅');
      resetForm();
      setShowModal(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const requestDelete = (id, name) => setDeleteConfirm({ show: true, id, name, force: false });

  const confirmDelete = async () => {
    const { id, force } = deleteConfirm;
    try {
      const url = force
        ? `/api/categories/${id}?force=true`
        : `/api/categories/${id}`;
      const result = await AxiosInstance.delete(url);

      if (result.data?.message?.includes('sous-catégories') && !force) {
        return setDeleteConfirm({ ...deleteConfirm, force: true });
      }

      viewMode === 'hierarchy' ? await fetchHierarchy() : await fetchCategories();
      setSuccessMessage('Catégorie supprimée avec succès ✅');
    } catch (err) {
      setError(err.message);
    } finally {
      setDeleteConfirm({ show: false, id: null, name: '', force: false });
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      icon: item.icon || '',
      isActive: item.isActive !== false,
      parentCategory: item.parentCategory?._id || item.parentCategory || '',
      order: item.order || 0,
    });
    setShowModal(true);
  };

  const resetForm = () => setFormData({ title: '', description: '', icon: '', isActive: true, parentCategory: '', order: 0 });

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const filteredCategories = categories.filter(cat =>
    cat.title.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    (cat.description && cat.description.toLowerCase().includes(debouncedSearchTerm.toLowerCase()))
  );

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCategories = filteredCategories.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCategories.length / itemsPerPage);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  if (loading) return <div className="loading-container"><div className="loading-spinner"></div></div>;

  return (
    <div className="arrondissement-container">
      <div className="container mx-auto px-4 py-4">
        {/* Header */}
        <div className="card page-header">
          <div className="header-content">
            <h1 className="header-title">Gestion des Catégories</h1>
            <button onClick={() => { resetForm(); setEditingItem(null); setShowModal(true); }} className="add-button">
              <Plus className="add-button-icon" /> Nouvelle Catégorie
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="card filters-section">
          <div className="search-container">
            <Search className="search-icon" />
            <input
              type="text"
              placeholder="Rechercher une catégorie..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {/* Table */}
        <div className="table-container">
          {/* Pagination Controls */}
          <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <label>Afficher </label>
              <select
                value={itemsPerPage}
                onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
              >
                {ITEMS_PER_PAGE_OPTIONS.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
              <span> éléments</span>
            </div>
            <div>
              {filteredCategories.length > 0 && (
                <span>
                  Affichage {(currentPage - 1) * itemsPerPage + 1} à {Math.min(currentPage * itemsPerPage, filteredCategories.length)} sur {filteredCategories.length}
                </span>
              )}
            </div>
          </div>

          <div className="table-scroll">
            <table className="data-table">
              <thead className="table-header">
                <tr>
                  <th>Titre</th>
                  <th>Description</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="table-body">
                {currentCategories.length === 0 ? (
                  <tr>
                    <td colSpan="3" className="empty-state">
                      <p className="empty-title">Aucune catégorie trouvée</p>
                      <p className="empty-subtitle">Essayez de modifier vos critères de recherche</p>
                    </td>
                  </tr>
                ) : currentCategories.map(cat => (
                  <tr key={cat._id} className="table-row">
                    <td className="table-cell">{cat.title}</td>
                    <td className="table-cell">{cat.description || 'Aucune description'}</td>
                    <td className="table-cell text-right">
                      <div className="actions-container">
                        <button onClick={() => handleEdit(cat)} className="action-button edit" title="Modifier">
                          <Edit2 className="action-icon" />
                        </button>
                        <button onClick={() => requestDelete(cat._id, cat.title)} className="action-button delete" title="Supprimer">
                          <Trash2 className="action-icon" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-container">
            <button disabled={currentPage === 1} onClick={() => setCurrentPage(prev => prev - 1)}>Précédent</button>
            {pageNumbers.map(number => (
              <button
                key={number}
                onClick={() => setCurrentPage(number)}
                className={currentPage === number ? 'active' : ''}
              >
                {number}
              </button>
            ))}
            <button disabled={currentPage === totalPages} onClick={() => setCurrentPage(prev => prev + 1)}>Suivant</button>
          </div>
        </div>

        {/* Stats */}
        <div className="card stats-section">
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-number blue">{categories.length}</div>
              <div className="stat-label">Total Catégories</div>
            </div>
            <div className="stat-item">
              <div className="stat-number green">{categories.filter(c => c.isActive !== false).length}</div>
              <div className="stat-label">Actives</div>
            </div>
            <div className="stat-item">
              <div className="stat-number orange">{categories.filter(c => c.parentCategory).length}</div>
              <div className="stat-label">Sous-catégories</div>
            </div>
          </div>
        </div>

        {/* Modals */}
        {showModal && (
          <CategoryModal
            editingItem={editingItem}
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleSubmit}
            onClose={() => { setShowModal(false); resetForm(); }}
          />
        )}

        {successMessage && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">Succès</h2>
              </div>
              <div className="modal-body">
                <p>{successMessage}</p>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">Erreur</h2>
                <button className="modal-close" onClick={() => setError('')}><X className="modal-close-icon" /></button>
              </div>
              <div className="modal-body">
                <p>{error}</p>
              </div>
            </div>
          </div>
        )}

        {deleteConfirm.show && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h2 className="modal-title">Confirmation</h2>
                <button className="modal-close" onClick={() => setDeleteConfirm({ show: false, id: null, name: '', force: false })}><X className="modal-close-icon" /></button>
              </div>
              <div className="modal-body">
                <p>{deleteConfirm.force ? `Cette catégorie a des sous-catégories. Voulez-vous forcer la suppression de "${deleteConfirm.name}" ?` : `Voulez-vous vraiment supprimer la catégorie "${deleteConfirm.name}" ?`}</p>
                <div className="modal-actions">
                  <button onClick={() => setDeleteConfirm({ show: false, id: null, name: '', force: false })}>Annuler</button>
                  <button onClick={confirmDelete}>Supprimer</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Modale stable
const CategoryModal = ({ editingItem, formData, setFormData, onSubmit, onClose }) => {
  return (
    <div className="modal-overlay">
      <div className="modal-content large">
        <div className="modal-header">
          <h2 className="modal-title">{editingItem ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</h2>
          <button onClick={onClose} className="modal-close"><X className="modal-close-icon" /></button>
        </div>
        <div className="modal-body">
          <form onSubmit={onSubmit}>
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label">Titre *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={e => setFormData({ ...formData, title: e.target.value })}
                  className="form-input"
                  placeholder="Titre"
                />
              </div>
              <div className="form-group">
                <label className="form-label">Icône</label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={e => setFormData({ ...formData, icon: e.target.value })}
                  className="form-input"
                  placeholder="Icône (emoji ou code)"
                />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                value={formData.description}
                onChange={e => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                className="form-textarea"
                placeholder="Description"
              />
            </div>
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={formData.isActive}
                  onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                  className="form-checkbox"
                />
                Catégorie active
              </label>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={onClose} className="modal-button cancel">Annuler</button>
              <button type="submit" className="modal-button submit">{editingItem ? 'Modifier' : 'Créer'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Categories;
